import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { AgingBucket, ContractFigures, Currency, InvoiceKind, InvoiceStatus, Obligation, ServiceContract, ServiceContractStatus, VariationStatus } from '@mnc/contracts';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';
import { stripBidiMarks } from '../../lib/text';

type Tone = 'neutral' | 'brand' | 'warn' | 'danger' | 'info';
export const CONTRACT_TONE: Record<ServiceContractStatus, Tone> = { draft: 'info', active: 'brand', on_hold: 'warn', completed: 'neutral', terminated: 'danger' };
export const INVOICE_TONE: Record<InvoiceStatus, Tone> = { draft: 'info', submitted: 'warn', approved: 'brand', paid: 'neutral', cancelled: 'danger' };
export const VARIATION_TONE: Record<VariationStatus, Tone> = { draft: 'info', submitted: 'warn', approved: 'brand', rejected: 'danger' };
export const BUCKET_TONE: Record<AgingBucket, Tone> = { current: 'neutral', d1_30: 'info', d31_60: 'warn', d61_90: 'warn', d90_plus: 'danger' };

export const fmtDate = (iso: string | null | undefined, language: string, empty = '—') => (iso ? stripBidiMarks(new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(language, { dateStyle: 'medium', timeZone: 'UTC' })) : empty);

/**
 * Every amount crosses the wire as an integer of minor units and is divided here, once. `sign: false` prints the
 * figure without the currency, for dense tables whose header already names it.
 */
export function useMoney(currency: Currency = 'SAR') {
  const { i18n } = useTranslation();
  return (minor: number | null | undefined, opts: { sign?: boolean } = {}) => {
    if (minor === null || minor === undefined) return '—';
    const value = minor / 100;
    return new Intl.NumberFormat(i18n.language, opts.sign === false ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  };
}

export const useQuantity = () => {
  const { i18n } = useTranslation();
  return (q: number | null | undefined) => (q === null || q === undefined ? '—' : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 4 }).format(q));
};

export function Money({ minor, currency, className, sign }: { minor: number | null | undefined; currency?: Currency; className?: string; sign?: boolean }) {
  const money = useMoney(currency);
  return <span className={cn('tabular-nums', className)} dir="ltr">{money(minor, { sign })}</span>;
}

export const ContractStatusBadge = ({ status }: { status: ServiceContractStatus }) => {
  const { t } = useTranslation();
  return <Badge tone={CONTRACT_TONE[status]}>{t(`contracts.statuses.${status}`)}</Badge>;
};

export const InvoiceStatusBadge = ({ status }: { status: InvoiceStatus }) => {
  const { t } = useTranslation();
  return <Badge tone={INVOICE_TONE[status]}>{t(`contracts.invoiceStatuses.${status}`)}</Badge>;
};

export const KindBadge = ({ kind }: { kind: InvoiceKind }) => {
  const { t } = useTranslation();
  return <Badge tone={kind === 'progress' ? 'neutral' : 'info'}>{t(`contracts.invoiceKinds.${kind}`)}</Badge>;
};

export const clientName = (c: Pick<ServiceContract, 'client'>, language: string): string => (c.client.entity ? pick(language, c.client.entity.name_en, c.client.entity.name_ar) : (c.client.name ?? '—'));

/**
 * Whether retention belongs on this contract's screens: a percentage in the terms, or money held or released before
 * the terms changed. The consultancy's own contracts hold none (owner, 2026-09-22), so the columns stay out of the way.
 */
export const usesRetention = (c: Pick<ServiceContract, 'retention_percent' | 'figures'>): boolean => c.retention_percent > 0 || c.figures.retention_held_minor !== 0 || c.figures.retention_released_minor !== 0;

/** A labelled figure; `hint` carries the second line (what it is made of). */
export function Figure({ label, children, hint, tone }: { label: string; children: React.ReactNode; hint?: string; tone?: 'warn' | 'danger' }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className={cn('text-sm font-semibold', tone === 'danger' && 'text-rose-600 dark:text-rose-400', tone === 'warn' && 'text-amber-600 dark:text-amber-400')}>{children}</div>
      {hint && <div className="text-xs text-neutral-500">{hint}</div>}
    </div>
  );
}

/** The contract's money at a glance: what it is worth, what is certified, what is held back and what is owed. */
export function FiguresPanel({ figures: f, currency, retention = true }: { figures: ContractFigures; currency: Currency; retention?: boolean }) {
  const { t } = useTranslation();
  const money = useMoney(currency);
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
      <Figure label={t('contracts.figures.revisedValue')} hint={`${t('contracts.figures.withVat')}: ${money(f.revised_with_vat_minor)}${f.variations_value_minor ? ` · ${t('contracts.figures.originalValue')}: ${money(f.original_value_minor)}` : ''}`}>{money(f.revised_value_minor)}</Figure>
      <Figure label={t('contracts.figures.certified')} hint={`${new Intl.NumberFormat().format(f.percent_certified)}%`}>
        <span className="flex items-center gap-2">
          {money(f.certified_minor)}
          <span className="h-1.5 w-12 overflow-hidden rounded bg-neutral-200 dark:bg-neutral-700"><span className="block h-full bg-brand-500" style={{ width: `${Math.min(100, Math.max(0, f.percent_certified))}%` }} /></span>
        </span>
      </Figure>
      {retention && <Figure label={t('contracts.figures.retentionHeld')} hint={f.retention_released_minor ? `${t('contracts.figures.retentionReleased')}: ${money(f.retention_released_minor)}` : undefined}>{money(f.retention_held_minor)}</Figure>}
      <Figure label={t('contracts.figures.advance')} hint={`${t('contracts.figures.advanceRecovered')}: ${money(f.advance_recovered_minor)}`}>{money(f.advance_invoiced_minor)}</Figure>
      <Figure label={t('contracts.figures.netInvoiced')} hint={`${t('contracts.figures.vat')}: ${money(f.vat_invoiced_minor)}`}>{money(f.net_invoiced_minor)}</Figure>
      <Figure label={t('contracts.figures.paid')}>{money(f.paid_minor)}</Figure>
      <Figure label={t('contracts.figures.outstanding')} tone={f.outstanding_minor > 0 ? 'warn' : undefined}>{money(f.outstanding_minor)}</Figure>
      <Figure label={t('contracts.figures.overdue')} tone={f.overdue_minor > 0 ? 'danger' : undefined}>{money(f.overdue_minor)}</Figure>
    </div>
  );
}

/** What a draft invoice must fix before it can be issued, in words rather than codes. */
export function Problems({ problems, quantityOf }: { problems: Array<{ boq_item_id: string | null; kind: string; available: number | null }>; quantityOf?: (id: string) => string }) {
  const { t } = useTranslation();
  if (!problems.length) return null;
  return (
    <ul className="grid gap-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
      {problems.map((p, i) => (
        <li key={`${p.kind}-${p.boq_item_id ?? i}`}>
          {t(`contracts.problems.${p.kind}`, { defaultValue: p.kind })}
          {p.boq_item_id && quantityOf ? ` — ${quantityOf(p.boq_item_id)}` : ''}
          {p.available !== null && p.boq_item_id ? ` (${t('contracts.available')}: ${new Intl.NumberFormat().format(p.available)})` : ''}
        </li>
      ))}
    </ul>
  );
}

/** A follow-up line: what is due, when, and by whom. */
export function ObligationLine({ o, showContract }: { o: Obligation; showContract?: boolean }) {
  const { t, i18n } = useTranslation();
  const late = o.days_to_due !== null && o.days_to_due < 0;
  return (
    <div className="min-w-0">
      <div className="truncate text-sm" dir="auto">
        {pick(i18n.language, o.title_en, o.title_ar)} <span className="text-xs text-neutral-500">· {t(`contracts.obligationKinds.${o.kind}`)}</span>
      </div>
      <div className="text-xs text-neutral-500">
        {showContract && (
          <>
            <Link to="/contracts/$id" params={{ id: o.contract.id }} className="hover:underline">{o.contract.reference}</Link> ·{' '}
          </>
        )}
        <span className={cn(late && 'font-medium text-rose-600 dark:text-rose-400')}>
          {fmtDate(o.due_date, i18n.language)}
          {o.days_to_due !== null && ` · ${late ? t('contracts.overdueBy', { days: Math.abs(o.days_to_due) }) : t('contracts.dueIn', { days: o.days_to_due })}`}
        </span>
        {o.owner && <> · {pick(i18n.language, o.owner.display_name_en, o.owner.display_name_ar)}</>}
      </div>
    </div>
  );
}
